import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import {
  IStoreGroupOption,
  IStoreHierarchy,
  IStoreOption,
  ITradingRegionOption,
  UNASSIGNED_GROUP_ID,
  UNASSIGNED_REGION_ID
} from '../glide-settings/glide-settings.service';

// GE-4543 UI rework. Presentational only: navigation state lives here, the selected ids stay with
// the parent form control so there is exactly one source of truth.
@Component({
  selector: 'app-store-hierarchy-selector',
  templateUrl: './store-hierarchy-selector.component.html',
  styleUrls: ['./store-hierarchy-selector.component.scss']
})
export class StoreHierarchySelectorComponent implements OnChanges {
  @Input() hierarchy: IStoreHierarchy = { companyName: '', tradingRegions: [] };
  @Input() selectedStoreIds: string[] = [];
  @Input() lockedStoreId: string | null = null;

  @Output() selectionChange = new EventEmitter<string[]>();

  selectedRegionId: string | null = null;
  selectedGroupId: string | null = null;
  filterText = '';

  readonly unassignedRegionId = UNASSIGNED_REGION_ID;
  readonly unassignedGroupId = UNASSIGNED_GROUP_ID;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['hierarchy']) {
      // The hierarchy input can be re-emitted by an unrelated update to the hot user-state
      // selector backing it (see glide-settings.service#getCompanyStoreHierarchy). Preserve the
      // operator's drill position when it is still valid rather than resetting to region 1 /
      // group 1 and blanking their filter mid-edit; only fall back when the current region or
      // group no longer exists in the new hierarchy.
      if (!this.selectedRegion) {
        this.selectFirstRegion();
      } else if (!this.selectedGroup) {
        this.selectFirstGroup();
      }
    }
  }

  get regions(): ITradingRegionOption[] {
    return this.hierarchy?.tradingRegions ?? [];
  }

  get selectedRegion(): ITradingRegionOption | null {
    return this.regions.find((region) => region.id === this.selectedRegionId) ?? null;
  }

  get storeGroups(): IStoreGroupOption[] {
    return this.selectedRegion?.storeGroups ?? [];
  }

  get selectedGroup(): IStoreGroupOption | null {
    return this.storeGroups.find((group) => group.id === this.selectedGroupId) ?? null;
  }

  get visibleStores(): IStoreOption[] {
    const stores = this.selectedGroup?.stores ?? [];
    const needle = this.filterText.trim().toLowerCase();
    return needle ? stores.filter((store) => store.name.toLowerCase().includes(needle)) : stores;
  }

  // True once the group genuinely has no stores at all -- as opposed to having stores that the
  // current filter just doesn't match, which needs different copy (no_matching_stores).
  get groupHasNoStores(): boolean {
    return !(this.selectedGroup?.stores.length ?? 0);
  }

  onRegionClick(regionId: string) {
    // Clicking the already-active region still clears the filter, per design -- only the
    // region/group selection itself is a no-op when unchanged.
    this.filterText = '';
    if (this.selectedRegionId === regionId) {
      return;
    }
    this.selectedRegionId = regionId;
    this.selectFirstGroup();
  }

  onGroupClick(groupId: string) {
    if (this.selectedGroupId === groupId) {
      return;
    }
    this.selectedGroupId = groupId;
    this.filterText = '';
  }

  isSelected(storeId: string): boolean {
    return (this.selectedStoreIds ?? []).includes(storeId);
  }

  isLocked(storeId: string): boolean {
    return !!this.lockedStoreId && this.lockedStoreId === storeId;
  }

  onStoreToggle(storeId: string, checked: boolean) {
    if (this.isLocked(storeId)) {
      return;
    }
    const selected = this.selectedStoreIds ?? [];
    this.selectionChange.emit(
      checked ? [...selected, storeId] : selected.filter((id) => id !== storeId)
    );
  }

  onFilterChange(value: string) {
    this.filterText = value ?? '';
  }

  get allVisibleSelected(): boolean {
    const visible = this.visibleStores;
    return visible.length > 0 && visible.every((store) => this.isSelected(store.id));
  }

  // Adds every store currently visible in the open group, or clears them once they are all
  // selected. The locked store is never removed.
  onSelectAllToggle() {
    const visibleIds = this.visibleStores.map((store) => store.id);
    const selected = this.selectedStoreIds ?? [];

    if (this.allVisibleSelected) {
      const removable = new Set(visibleIds.filter((id) => !this.isLocked(id)));
      this.selectionChange.emit(selected.filter((id) => !removable.has(id)));
      return;
    }

    this.selectionChange.emit([...new Set([...selected, ...visibleIds])]);
  }

  private selectFirstRegion() {
    this.selectedRegionId = this.regions[0]?.id ?? null;
    this.filterText = '';
    this.selectFirstGroup();
  }

  private selectFirstGroup() {
    this.selectedGroupId = this.storeGroups[0]?.id ?? null;
  }
}
